import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PageHeader } from '../components/ui';
import api from '../services/api';
import { isWineryManagedRole } from '../utils/authUser';
import { exportToExcel, ExcelColumn } from '../utils/exportToExcel';


interface Permission {
  name: string;
  description: string;
  enabled: boolean;
}

interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

interface Role {
  id: number;
  name: string;
  description?: string;
  permissions?: Record<string, boolean> | Array<{
    id: number;
    name: string;
    resource: string;
    action: string;
    description?: string;
  }>;
  Permissions?: Array<{
    id: number;
    name: string;
    resource: string;
    action: string;
    description?: string;
  }>;
}

interface RoleFormValues {
  name: string;
  description?: string;
  permissions: Record<string, boolean>;
}

// Helper function to build permission groups dynamically
const buildPermissionGroups = (permissions: Array<{ id: number; name: string; resource: string; action: string; description?: string }>): PermissionGroup[] => {
  // Normalize resource names to handle singular/plural variants and camelCase
  const normalizeResource = (resource: string): string => {
    const normalized = resource.toLowerCase();
    // Map both singular and plural to a common key
    const variants: Record<string, string> = {
      'user': 'users',
      'users': 'users',
      'role': 'roles',
      'roles': 'roles',
      'winebatch': 'winebatches',
      'winebatches': 'winebatches',
      'vineyard': 'vineyards',
      'vineyards': 'vineyards',
      'vessel': 'vessels',
      'vessels': 'vessels',
      'fermentation': 'fermentations',
      'fermentations': 'fermentations',
      'fermentationmaintenance': 'fermentationmaintenance',
      'agingmaintenance': 'agingmaintenance',
      'vineyardmaintenance': 'vineyardmaintenance',
      'postfermentation': 'postfermentation',
      'aging': 'aging',
      'bottling': 'bottling',
      'permissions': 'permissions',
      'permission': 'permissions'
    };
    return variants[normalized] || normalized;
  };

  // Group permissions by normalized resource
  const grouped: Record<string, Permission[]> = {};

  // Track which actions we've already added for each resource to avoid duplicates
  const addedActions: Record<string, Set<string>> = {};

  permissions.forEach(perm => {
    // Skip global permissions and unwanted sections
    const skipResources = ['all', 'view_all', 'edit_all', 'admin', 'cellar', 'view_cellar', 'production', 'treatment', 'batch', 'batches', 'permissions', 'permission', 'platform'];
    if (skipResources.includes(perm.resource.toLowerCase())) {
      return;
    }

    // Skip delete permissions
    if (perm.action === 'delete') {
      return;
    }

    const normalizedResource = normalizeResource(perm.resource);

    if (!grouped[normalizedResource]) {
      grouped[normalizedResource] = [];
      addedActions[normalizedResource] = new Set();
    }

    // Skip if we already have this action for this resource
    if (addedActions[normalizedResource].has(perm.action)) {
      return;
    }

    addedActions[normalizedResource].add(perm.action);
    const description = perm.description || `${perm.action} ${perm.resource}`;
    grouped[normalizedResource].push({
      name: perm.name,
      description: description,
      enabled: false
    });
  });

  // Convert to permission groups with nice display names (using normalized keys)
  const resourceDisplayNames: Record<string, string> = {
    'users': 'Users',
    'roles': 'Roles',
    'winebatches': 'Wine Batches',
    'batches': 'Batches',
    'vineyards': 'Vineyards',
    'vessels': 'Vessels',
    'fermentations': 'Fermentation',
    'fermentationmaintenance': 'Fermentation Maintenance',
    'aging': 'Aging',
    'agingmaintenance': 'Aging Maintenance',
    'bottling': 'Bottling',
    'postfermentation': 'Post Fermentation',
    'vineyardmaintenance': 'Vineyard Maintenance',
    'permissions': 'Permissions'
  };

  // Sort resources in the specified order
  const priorityOrder = [
    'vineyards',
    'vineyardmaintenance',
    'winebatches',
    'fermentations',
    'fermentationmaintenance',
    'postfermentation',
    'aging',
    'agingmaintenance',
    'vessels',
    'users',
    'roles',
    'permissions'
  ];
  const sortedResources = Object.keys(grouped).sort((a, b) => {
    const aIdx = priorityOrder.indexOf(a);
    const bIdx = priorityOrder.indexOf(b);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedResources.map(resource => ({
    name: resourceDisplayNames[resource] || resource.charAt(0).toUpperCase() + resource.slice(1),
    permissions: grouped[resource]
  }));
};

const RoleSchema = Yup.object().shape({
  name: Yup.string().required('Role name is required'),
  description: Yup.string(),
});

const Roles: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [allPermissions, setAllPermissions] = useState<Array<{ id: number; name: string; resource: string; action: string }>>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch roles and permissions in parallel
        const [rolesResponse, permissionsResponse] = await Promise.all([
          api.get('/api/roles'),
          api.get('/api/permissions')
        ]);

        // Transform roles data to convert Permissions array to permissions object.
        // Hide platform_owner from winery Role Management UI.
        const transformedRoles = rolesResponse.data
          .filter((role: any) => isWineryManagedRole(role))
          .map((role: any) => {
            const permissionsObj: Record<string, boolean> = {};
            // Note: API interceptor converts Permissions to permissions (camelCase)
            const permissionsArray = role.permissions || role.Permissions || [];
            if (Array.isArray(permissionsArray)) {
              permissionsArray.forEach((perm: any) => {
                permissionsObj[perm.name] = true;
              });
            }
            return {
              ...role,
              permissions: permissionsObj
            };
          });

        setRoles(transformedRoles);
        setAllPermissions(permissionsResponse.data);

        // Build permission groups from fetched permissions
        const groups = buildPermissionGroups(permissionsResponse.data);
        setPermissionGroups(groups);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [tabVisible]);

  const openModal = (role: Role | null = null) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
  };

  const handleSubmit = async (values: RoleFormValues, { resetForm }: any) => {
    try {
      // Convert permissions object to permissionIds array
      const permissionIds: number[] = [];
      Object.keys(values.permissions).forEach(permName => {
        if (values.permissions[permName]) {
          const permission = allPermissions.find(p => p.name === permName);
          if (permission) {
            permissionIds.push(permission.id);
          }
        }
      });

      const payload = {
        name: values.name,
        description: values.description,
        permissionIds
      };

      if (editingRole) {
        // Update existing role
        const response = await api.put(`/api/roles/${editingRole.id}`, payload);

        // Transform response data
        const permissionsObj: Record<string, boolean> = {};
        // Note: API interceptor converts Permissions to permissions (camelCase)
        const permissionsArray = response.data.permissions || response.data.Permissions || [];
        if (Array.isArray(permissionsArray)) {
          permissionsArray.forEach((perm: any) => {
            permissionsObj[perm.name] = true;
          });
        }
        const transformedRole = {
          ...response.data,
          permissions: permissionsObj
        };

        setRoles(roles.map(role => (role.id === editingRole.id ? transformedRole : role)));
        setSuccessMessage('Role updated successfully!');
      } else {
        // Create new role
        const response = await api.post('/api/roles', payload);

        // Transform response data
        const permissionsObj: Record<string, boolean> = {};
        // Note: API interceptor converts Permissions to permissions (camelCase)
        const permissionsArray = response.data.permissions || response.data.Permissions || [];
        if (Array.isArray(permissionsArray)) {
          permissionsArray.forEach((perm: any) => {
            permissionsObj[perm.name] = true;
          });
        }
        const transformedRole = {
          ...response.data,
          permissions: permissionsObj
        };

        setRoles([...roles, transformedRole]);
        setSuccessMessage('Role created successfully!');
      }
      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving role:', err);
      setError(err);
    }
  };


  // Initialize permissions from existing role or create new permissions object
  const getInitialValues = (role: Role | null): RoleFormValues => {
    if (role) {
      // Handle case where permissions might still be an array (before transformation)
      let permissionsObj: Record<string, boolean> = {};
      if (role.permissions) {
        if (Array.isArray(role.permissions)) {
          role.permissions.forEach((perm: any) => {
            permissionsObj[perm.name] = true;
          });
        } else {
          permissionsObj = role.permissions;
        }
      }

      return {
        name: role.name,
        description: role.description,
        permissions: permissionsObj,
      };
    }

    // Create empty permissions object with all permissions set to false
    const initialPermissions: Record<string, boolean> = {};
    permissionGroups.forEach(group => {
      group.permissions.forEach(permission => {
        initialPermissions[permission.name] = false;
      });
    });

    return {
      name: '',
      description: '',
      permissions: initialPermissions,
    };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Export roles to Excel (no internal permission IDs, only summary info).
  const handleExportToExcel = () => {
    if (!roles.length) return;

    const columns: ExcelColumn<Role>[] = [
      { header: 'Name', accessor: (r) => r.name },
      { header: 'Description', accessor: (r) => r.description || '' },
      {
        header: 'Permissions Count',
        accessor: (r) => {
          const perms = r.permissions;
          if (!perms) return 0;
          if (Array.isArray(perms)) return perms.length;
          return Object.values(perms).filter(Boolean).length;
        },
      },
    ];

    exportToExcel({
      fileName: `roles-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'Roles',
      rows: roles,
      columns,
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Role Management"
        subtitle="Define roles and their associated permissions."
        variant="compact"
        actions={(
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleExportToExcel}
            className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
          >
            Export to Excel
          </button>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
          >
            Add Role
          </button>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-monday-border">
                <thead className="bg-monday-header">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-monday-border-light bg-white">
                  {roles.map((role) => (
                    <tr key={role.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                        {role.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                        {role.description}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <button
                          onClick={() => openModal(role)}
                          className="text-primary-500 hover:text-primary-600"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingRole ? 'Edit Role' : 'Create New Role'} size="full">
        <div className="mobile-form-container">
          <Formik
            key={editingRole?.id || 'new'}
            enableReinitialize={true}
            initialValues={getInitialValues(editingRole)}
            validationSchema={RoleSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, handleChange, handleBlur }) => (
              <Form className="space-y-6">
                <div>
                  <label htmlFor="name" className="form-label">
                    Role Name
                  </label>
                  <Field type="text" name="name" id="name" className="mobile-form-input" />
                  <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label htmlFor="description" className="form-label">
                    Description <span className="text-monday-text-muted font-normal">(optional)</span>
                  </label>
                  <Field as="textarea" name="description" id="description" rows={3} className="mobile-form-input resize-none" />
                  <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <h4 className="text-lg font-medium text-monday-text">Permissions</h4>
                  <p className="text-sm text-monday-text-muted">
                    Select the permissions for this role
                  </p>

                  {/* Global action buttons */}
                  <div className="mt-4 mb-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-full"
                      onClick={() => {
                        const newPermissions = { ...values.permissions };

                        // Check if all view permissions are already enabled
                        const viewPerms = allPermissions.filter(perm => perm.action === 'read' || perm.action === 'view');
                        const allViewChecked = viewPerms.every(perm => values.permissions[perm.name]);

                        // Toggle: if all checked, uncheck all; otherwise check all
                        viewPerms.forEach(perm => {
                          newPermissions[perm.name] = !allViewChecked;
                        });
                        setFieldValue('permissions', newPermissions);
                      }}
                    >
                      View All
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                      onClick={() => {
                        const newPermissions = { ...values.permissions };

                        // Check if all create permissions are already enabled
                        const createPerms = allPermissions.filter(perm => perm.action === 'create');
                        const allCreateChecked = createPerms.every(perm => values.permissions[perm.name]);

                        // Toggle: if all checked, uncheck all; otherwise check all
                        createPerms.forEach(perm => {
                          newPermissions[perm.name] = !allCreateChecked;
                        });
                        setFieldValue('permissions', newPermissions);
                      }}
                    >
                      Create All
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md"
                      onClick={() => {
                        const newPermissions = { ...values.permissions };

                        // Check if all update permissions are already enabled
                        const updatePerms = allPermissions.filter(perm => perm.action === 'update' || perm.action === 'edit');
                        const allUpdateChecked = updatePerms.every(perm => values.permissions[perm.name]);

                        // Toggle: if all checked, uncheck all; otherwise check all
                        updatePerms.forEach(perm => {
                          newPermissions[perm.name] = !allUpdateChecked;
                        });
                        setFieldValue('permissions', newPermissions);
                      }}
                    >
                      Update All
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium text-monday-text bg-monday-header hover:bg-monday-hover rounded-md"
                      onClick={() => {
                        const newPermissions = { ...values.permissions };
                        Object.keys(newPermissions).forEach(key => {
                          newPermissions[key] = false;
                        });
                        setFieldValue('permissions', newPermissions);
                      }}
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {permissionGroups.map((group) => (
                      <div key={group.name} className="bg-monday-header p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-md font-medium text-monday-text">{group.name}</h5>
                          <div className="flex items-center">
                            <label className="inline-flex items-center text-sm text-monday-text mr-4">
                              <input
                                type="checkbox"
                                className="form-checkbox h-4 w-4 text-primary-500"
                                onChange={(e) => {
                                  // Select/deselect all permissions in this group
                                  const isChecked = e.target.checked;
                                  const newPermissions = { ...values.permissions };
                                  group.permissions.forEach((permission) => {
                                    newPermissions[permission.name] = isChecked;
                                  });
                                  setFieldValue('permissions', newPermissions);
                                }}
                                checked={group.permissions.every(
                                  (permission) => values.permissions[permission.name]
                                )}
                              />
                              <span className="ml-2">Select All</span>
                            </label>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {group.permissions.map((permission) => (
                            <div key={permission.name} className="flex items-center">
                              <input
                                id={permission.name}
                                name={`permissions.${permission.name}`}
                                type="checkbox"
                                checked={values.permissions[permission.name] || false}
                                onChange={(e) => {
                                  const newPermissions = { ...values.permissions };
                                  newPermissions[permission.name] = e.target.checked;
                                  setFieldValue('permissions', newPermissions);
                                }}
                                onBlur={handleBlur}
                                className="h-4 w-4 text-primary-500 focus:ring-primary-200 border-monday-border rounded"
                              />
                              <label
                                htmlFor={permission.name}
                                className="ml-3 block text-sm text-monday-text"
                              >
                                {permission.description}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" className="mobile-btn mobile-btn-primary w-full sm:w-auto">
                    {editingRole ? 'Update' : 'Create'}
                  </button>
                  <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default Roles; 